import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { extractColors } from 'extract-colors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  authApi,
  businessApi,
  billingApi,
  templateConfigApi,
  uploadApi,
  documentsApi,
  type TemplateMetadata,
} from '@/lib/api';
import { pricingPlans } from '@/data/pricingPlans';
import type { OnboardingCheckoutMode } from '@/lib/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
import { ALL_INDUSTRIES, POPULAR_INDUSTRIES as POPULAR_IDS } from '@/data/industries';
import {
  SAMPLE_INVOICE,
  SAMPLE_BUSINESS,
} from '@/components/TemplateDesigner/sampleData';
import { applyIndustryOverrides } from '@/data/industrySampleData';
import {
  DEFAULT_TEMPLATE_SETTINGS,
  type ColorTheme,
  type TemplateSettings,
} from '@/components/TemplateDesigner/types';
import {
  generateThemeFromPrimary,
  analyzeExtractedColors,
  type ExtractedColor,
} from '@/components/TemplateDesigner/utils';
import { buildOwlOpsData, injectColorOverride, updateIframeColors } from '@/components/TemplateDesigner/DocumentPreview';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  Check,
  ChevronDown,
  Loader2,
  Upload,
  X,
  Shield,
  LockKeyhole,
  Sparkles,
  CreditCard,
} from 'lucide-react';
import { HexColorPicker } from 'react-colorful';

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

interface OnboardingData {
  firstName: string;
  lastName: string;
  businessName: string;
  industry: string;
  industryOther: string;
  address: string;
  addressUnit: string;
  addressCoords: { latitude: number; longitude: number } | null;
  templateSlug: string;
  colorTheme: string;
  logoFile: File | null;
  logoPreview: string | null;
  logoExtractedColors: ExtractedColor[];
}

const INITIAL_DATA: OnboardingData = {
  firstName: '',
  lastName: '',
  businessName: '',
  industry: '',
  industryOther: '',
  address: '',
  addressUnit: '',
  addressCoords: null,
  templateSlug: 'standard',
  colorTheme: '#2671E8',
  logoFile: null,
  logoPreview: null,
  logoExtractedColors: [],
};

const COLOR_THEMES = [
  // Blues
  { id: 'sky', hex: '#0EA5E9', name: 'Sky' },
  { id: 'blue', hex: '#2671E8', name: 'Blue' },
  { id: 'navy', hex: '#1E40AF', name: 'Navy' },
  { id: 'royal', hex: '#3730A3', name: 'Royal' },
  { id: 'indigo', hex: '#6366F1', name: 'Indigo' },
  // Purples & Pinks
  { id: 'violet', hex: '#7C3AED', name: 'Violet' },
  { id: 'purple', hex: '#9333EA', name: 'Purple' },
  { id: 'fuchsia', hex: '#D946EF', name: 'Fuchsia' },
  { id: 'pink', hex: '#EC4899', name: 'Pink' },
  { id: 'rose', hex: '#F43F5E', name: 'Rose' },
  // Reds & Oranges
  { id: 'red', hex: '#EF4444', name: 'Red' },
  { id: 'vermilion', hex: '#DC2626', name: 'Vermilion' },
  { id: 'orange', hex: '#F97316', name: 'Orange' },
  { id: 'amber', hex: '#F59E0B', name: 'Amber' },
  { id: 'gold', hex: '#CA8A04', name: 'Gold' },
  // Greens
  { id: 'lime', hex: '#84CC16', name: 'Lime' },
  { id: 'green', hex: '#10B981', name: 'Green' },
  { id: 'emerald', hex: '#059669', name: 'Emerald' },
  { id: 'forest', hex: '#15803D', name: 'Forest' },
  { id: 'teal', hex: '#14B8A6', name: 'Teal' },
  // Cyans & Neutrals
  { id: 'cyan', hex: '#06B6D4', name: 'Cyan' },
  { id: 'steel', hex: '#0891B2', name: 'Steel' },
  { id: 'slate', hex: '#475569', name: 'Slate' },
  { id: 'graphite', hex: '#374151', name: 'Graphite' },
  { id: 'midnight', hex: '#1E293B', name: 'Midnight' },
  // Warm neutrals
  { id: 'brown', hex: '#92400E', name: 'Brown' },
  { id: 'clay', hex: '#9A3412', name: 'Clay' },
  { id: 'wine', hex: '#881337', name: 'Wine' },
  { id: 'plum', hex: '#701A75', name: 'Plum' },
];

const POPULAR_INDUSTRY_DATA = ALL_INDUSTRIES.filter((i) => POPULAR_IDS.includes(i.id));

const STEP_LABELS = ['Step 1', 'Step 2', 'Step 3'];

// ---------------------------------------------------------------------------
// Live Template Preview (iframe-based, uses Owl Ops API)
// ---------------------------------------------------------------------------

function useTemplatePreview(
  templateSlug: string,
  colorTheme: ColorTheme,
  businessName: string,
  address: string,
  logoPreview: string | null,
  industry: string,
) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const settings: TemplateSettings = useMemo(() => ({
    ...DEFAULT_TEMPLATE_SETTINGS,
    selectedLayout: templateSlug,
    colorTheme,
    logo: {
      ...DEFAULT_TEMPLATE_SETTINGS.logo,
      url: logoPreview || 'icon:initials',
    },
  }), [templateSlug, colorTheme, logoPreview]);

  const business = useMemo(() => ({
    ...SAMPLE_BUSINESS,
    name: businessName || SAMPLE_BUSINESS.name,
    address: address || SAMPLE_BUSINESS.address,
    logo: logoPreview || undefined,
  }), [businessName, address, logoPreview]);

  // Re-render key: changes when we need a full API re-render (slug, logo, industry, or initials change)
  const initialsKey = logoPreview ? '' : businessName;
  const renderKey = `${templateSlug}|${logoPreview || ''}|${industry}|${initialsKey}`;
  const lastRenderKeyRef = useRef('');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const needsFullRender = lastRenderKeyRef.current !== renderKey || !html;
    if (!needsFullRender) return;

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data: apiData, settings: apiSettings } = buildOwlOpsData(
          SAMPLE_INVOICE, business, settings, 'invoice'
        );
        applyIndustryOverrides(apiData, industry, businessName, address);
        const response = await documentsApi.renderHtml({
          template_slug: templateSlug,
          data: apiData,
          settings: apiSettings,
        });
        if (response.data?.html) {
          setHtml(response.data.html);
          lastRenderKeyRef.current = renderKey;
        }
      } catch (err) {
        console.error('Preview render failed:', err);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [renderKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !html) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.documentElement) {
        updateIframeColors(doc, colorTheme);
      }
    } catch { /* ignore */ }
  }, [colorTheme, html]);

  const finalHtml = useMemo(() => {
    if (!html) return '';
    return injectColorOverride(html, colorTheme);
  }, [html]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    try {
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (doc?.documentElement) {
        updateIframeColors(doc, colorTheme);
      }
    } catch { /* ignore */ }
  }, [colorTheme]);

  return { finalHtml, loading, iframeRef, handleLoad };
}

// ---------------------------------------------------------------------------
// Mock Invoice Card (steps 1-2 right pane)
// ---------------------------------------------------------------------------

function MockInvoiceCard({ data }: { data: OnboardingData }) {
  const color = data.colorTheme;
  const biz = data.businessName || 'Your Business';
  const rawAddr = data.address || '123 Main Street\nYour City, ST 12345';
  const unit = data.addressUnit ? `, ${data.addressUnit}` : '';
  const addr = unit ? rawAddr.replace('\n', `${unit}\n`) : rawAddr;
  const initials = biz.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'YB';
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 14);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="relative mb-10">
      <div
        className="absolute -top-3 -right-3 z-20 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg transition-colors duration-500"
        style={{ backgroundColor: color }}
      >
        PAID
      </div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[380px] overflow-hidden transform hover:rotate-0 rotate-1 transition-all duration-500 hover:shadow-3xl">
        <div className="px-6 py-5 transition-colors duration-500" style={{ backgroundColor: color }}>
          <div className="flex justify-between items-start">
            <div>
              <div className="w-11 h-11 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center mb-1.5">
                <span className="text-white font-bold text-sm">{initials}</span>
              </div>
              <p className="text-white font-semibold text-sm">{biz}</p>
              <p className="text-white/60 text-xs mt-0.5 whitespace-pre-line leading-relaxed">{addr}</p>
            </div>
            <div className="text-right">
              <p className="text-white/90 text-xl font-bold tracking-tight">INVOICE</p>
              <p className="text-white/50 text-xs">#INV-001</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex justify-between text-xs">
            <div>
              <p className="text-muted-foreground/70 uppercase tracking-wider text-[10px] font-medium">Bill To</p>
              <p className="font-medium text-foreground mt-0.5">Sarah Johnson</p>
              <p className="text-muted-foreground text-[11px]">sarah@example.com</p>
            </div>
            <div className="text-right">
              <p className="text-muted-foreground/70 uppercase tracking-wider text-[10px] font-medium">Due Date</p>
              <p className="font-medium text-foreground mt-0.5">{fmt(dueDate)}</p>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden border border-border/40">
            <div className="transition-colors duration-500" style={{ backgroundColor: color + '10' }}>
              <div className="flex justify-between gap-3 px-3.5 py-2 text-xs font-semibold transition-colors duration-500" style={{ color }}>
                <span>Description</span>
                <span>Amount</span>
              </div>
            </div>
            <div className="divide-y divide-border/30 text-xs">
              {[['Website Redesign', '$2,500.00'], ['SEO Optimization', '$750.00'], ['Monthly Hosting', '$50.00']].map(([desc, amt], i) => (
                <div key={desc} className={`flex justify-between gap-3 px-3.5 py-2.5 ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                  <span className="text-muted-foreground truncate min-w-0">{desc}</span>
                  <span className="text-foreground font-medium whitespace-nowrap">{amt}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-border/40">
            <span className="font-semibold text-sm text-foreground">Total</span>
            <span className="text-xl font-bold transition-colors duration-500" style={{ color }}>$3,300.00</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right Pane: Live Invoice Preview + Social Proof
// ---------------------------------------------------------------------------

function RightPane({ data, currentStep }: { data: OnboardingData; currentStep: number }) {
  const color = data.colorTheme;
  const colorTheme = useMemo(() => generateThemeFromPrimary(color), [color]);
  const showPreview = currentStep === 3 || currentStep === 5;

  const fullAddress = data.addressUnit && data.address
    ? data.address.replace('\n', `, ${data.addressUnit}\n`)
    : data.address;

  const { finalHtml, loading, iframeRef, handleLoad } = useTemplatePreview(
    data.templateSlug,
    colorTheme,
    data.businessName,
    fullAddress,
    data.logoPreview,
    data.industry,
  );

  return (
    <div className="hidden md:flex flex-1 relative overflow-hidden">
      <div
        className="absolute inset-0 transition-colors duration-700"
        style={{
          background: `linear-gradient(135deg, ${color}08 0%, ${color}15 50%, ${color}08 100%)`,
        }}
      />
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: color + '18' }}
      />
      <div
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: color + '12' }}
      />

      <div className={`relative z-10 flex-1 flex flex-col items-center justify-center ${showPreview ? 'p-6' : 'p-12'}`}>
        <div className="text-center mb-6">
          <h2 className={`font-bold text-foreground mb-1 ${showPreview ? 'text-xl' : 'text-3xl mb-2'}`}>
            {currentStep === 1 && 'See your invoice come to life'}
            {currentStep === 2 && 'Looking professional already'}
            {currentStep === 3 && 'Make it yours'}
            {currentStep === 5 && 'Ready to send'}
          </h2>
          <p className={`text-muted-foreground ${showPreview ? 'text-sm' : ''}`}>
            {currentStep === 1 && 'Your business details appear as you type'}
            {currentStep === 2 && 'Businesses on InvoiceOwl get paid 4x faster'}
            {currentStep === 3 && 'Your brand, your colors, your style'}
            {currentStep === 5 && 'Your personalized invoice is just one step away'}
          </p>
        </div>

        {showPreview ? (
          <div className="relative w-full max-w-[540px]">
            <div
              className="absolute -top-3 -right-3 z-20 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg transition-colors duration-500"
              style={{ backgroundColor: color }}
            >
              PREVIEW
            </div>

            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden aspect-[8.5/11] relative">
              {loading && !finalHtml ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="w-full h-full overflow-hidden">
                  <iframe
                    ref={iframeRef}
                    srcDoc={finalHtml}
                    onLoad={handleLoad}
                    className="border-0 pointer-events-none origin-top-left"
                    title="Invoice Preview"
                    sandbox="allow-same-origin"
                    style={{
                      width: '816px',
                      height: '1056px',
                      transform: 'scale(0.661)',
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              )}
              {loading && finalHtml && (
                <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <MockInvoiceCard data={data} />

            <div className="flex gap-6 text-center">
              <div>
                <p className="text-2xl font-bold whitespace-nowrap transition-colors duration-500" style={{ color }}>
                  50,000+
                </p>
                <p className="text-xs text-muted-foreground">Businesses trust us</p>
              </div>
              <div className="w-px bg-border" />
              <div>
                <p className="text-2xl font-bold whitespace-nowrap transition-colors duration-500" style={{ color }}>
                  3 days
                </p>
                <p className="text-xs text-muted-foreground">Avg. time to get paid</p>
              </div>
              <div className="w-px bg-border" />
              <div>
                <p className="text-2xl font-bold whitespace-nowrap transition-colors duration-500" style={{ color }}>
                  $2B+
                </p>
                <p className="text-xs text-muted-foreground">Invoices processed</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress Bar (minimal segmented bar)
// ---------------------------------------------------------------------------

function ProgressBar({ currentStep }: { currentStep: number }) {
  if (currentStep > 3) return null;

  return (
    <div className="flex gap-1.5">
      {STEP_LABELS.map((_, i) => {
        const stepNum = i + 1;
        const isComplete = currentStep > stepNum;
        const isActive = currentStep === stepNum;
        return (
          <div
            key={stepNum}
            className={`h-1 flex-1 rounded-full transition-all duration-500 ${
              isComplete || isActive ? 'bg-primary' : 'bg-border'
            }`}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: About You
// ---------------------------------------------------------------------------

function StepAboutYou({
  data,
  onChange,
}: {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
}) {
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => firstNameRef.current?.focus(), 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground tracking-tight">
          Welcome to InvoiceOwl
        </h2>
        <p className="text-muted-foreground mt-2 text-lg">
          Let's set up your account in under a minute
        </p>
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-sm font-medium">
              First Name
            </Label>
            <Input
              ref={firstNameRef}
              id="firstName"
              placeholder="John"
              value={data.firstName}
              onChange={(e) => onChange({ firstName: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && document.getElementById('lastName')?.focus()}
              className="h-12 rounded-xl"
              autoComplete="off"
              data-1p-ignore
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-sm font-medium">
              Last Name
            </Label>
            <Input
              id="lastName"
              placeholder="Smith"
              value={data.lastName}
              onChange={(e) => onChange({ lastName: e.target.value })}
              onKeyDown={(e) =>
                e.key === 'Enter' && document.getElementById('businessName')?.focus()
              }
              className="h-12 rounded-xl"
              autoComplete="off"
              data-1p-ignore
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="businessName" className="text-sm font-medium">
            Business Name
          </Label>
          <Input
            id="businessName"
            placeholder="e.g. Mike's Plumbing"
            value={data.businessName}
            onChange={(e) => onChange({ businessName: e.target.value })}
            className="h-12 rounded-xl"
            autoComplete="off"
            data-1p-ignore
          />
        </div>

        {/* Business Address */}
        <div className="space-y-3">
          <Label htmlFor="address" className="text-sm font-medium">
            Business Address
          </Label>
          <AddressAutocomplete
            id="address"
            placeholder="Start typing your business address..."
            value={data.address}
            onChange={(value, coords) => {
              onChange({ address: value, addressCoords: coords || null });
            }}
            rows={2}
            className="h-12 rounded-xl"
          />

          {/* Suite/Unit — appears after address is entered */}
          {data.address && (
            <div className="space-y-1.5">
              <Label htmlFor="addressUnit" className="text-xs text-muted-foreground font-medium">
                Unit / Suite <span className="text-muted-foreground/60">(optional)</span>
              </Label>
              <Input
                id="addressUnit"
                placeholder="e.g. Suite 100, Unit B, Apt 2"
                value={data.addressUnit}
                onChange={(e) => onChange({ addressUnit: e.target.value })}
                className="h-11 rounded-xl"
                autoComplete="off"
                data-1p-ignore
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Your Business (industry, address + suite, logo)
// ---------------------------------------------------------------------------

function StepYourBusiness({
  data,
  onChange,
}: {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
}) {
  const [industrySearch, setIndustrySearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedIndustryLabel = ALL_INDUSTRIES.find((i) => i.id === data.industry)?.label || '';
  const filteredIndustries = industrySearch
    ? ALL_INDUSTRIES.filter((i) =>
        i.label.toLowerCase().includes(industrySearch.toLowerCase()) ||
        i.category.toLowerCase().includes(industrySearch.toLowerCase())
      )
    : [];
  const visibleIndustries = filteredIndustries.slice(0, 10);

  const scrollToHighlighted = (index: number) => {
    const container = dropdownRef.current;
    if (!container) return;
    const items = container.children;
    if (items[index]) {
      (items[index] as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  };

  // Total dropdown items = visible industries + 1 "Other/Custom" option
  const dropdownCount = visibleIndustries.length + 1;

  const handleIndustryKeyDown = (e: React.KeyboardEvent) => {
    if (!industrySearch) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (highlightedIndex + 1) % dropdownCount;
      setHighlightedIndex(next);
      scrollToHighlighted(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = highlightedIndex <= 0 ? dropdownCount - 1 : highlightedIndex - 1;
      setHighlightedIndex(next);
      scrollToHighlighted(next);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      if (highlightedIndex < visibleIndustries.length) {
        onChange({ industry: visibleIndustries[highlightedIndex].id });
      } else {
        onChange({ industry: 'other', industryOther: industrySearch });
      }
      setIndustrySearch('');
      setHighlightedIndex(-1);
    } else if (e.key === 'Escape') {
      setIndustrySearch('');
      setHighlightedIndex(-1);
    }
  };

  const [logoError, setLogoError] = useState('');

  const processLogoFile = useCallback((file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError(`Unsupported file type. Please use PNG, JPG, GIF, or WebP.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      setLogoError(`File is too large (${sizeMB}MB). Maximum size is 5MB.`);
      return;
    }
    setLogoError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const preview = ev.target?.result as string;
      onChange({ logoFile: file, logoPreview: preview });

      extractColors(preview, { crossOrigin: 'anonymous' })
        .then((colors) => {
          const sorted = colors
            .filter((c) => c.area >= 0.03)
            .map((c) => ({ hex: c.hex.toLowerCase(), area: c.area }))
            .sort((a, b) => b.area - a.area);

          const seen = new Set<string>();
          const deduped: ExtractedColor[] = [];
          for (const color of sorted) {
            if (!seen.has(color.hex) && deduped.length < 10) {
              seen.add(color.hex);
              deduped.push(color);
            }
          }

          const { usableColors } = analyzeExtractedColors(deduped);
          onChange({ logoExtractedColors: usableColors.slice(0, 5) });
        })
        .catch(() => {
          onChange({ logoExtractedColors: [] });
        });
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) processLogoFile(file);
  }, [processLogoFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  return (
    <>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground tracking-tight">
          Tell us about your business
        </h2>
        <p className="text-muted-foreground mt-2 text-base">
          We'll customize your invoices for your trade
        </p>
      </div>

      <div className="space-y-7">
        {/* Industry */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">What do you do?</Label>

          {data.industry ? (
            <div className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl">
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <Check className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-sm font-medium flex-1">
                {data.industry === 'other' ? data.industryOther || 'Other' : selectedIndustryLabel}
              </span>
              <button
                type="button"
                onClick={() => onChange({ industry: '', industryOther: '' })}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Input
                  placeholder="Search industries..."
                  value={industrySearch}
                  onChange={(e) => {
                    setIndustrySearch(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  onKeyDown={handleIndustryKeyDown}
                  className="h-12 rounded-xl"
                  autoComplete="off"
                  data-1p-ignore
                />

                {industrySearch && (
                  <div ref={dropdownRef} className="absolute z-20 left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto border rounded-xl divide-y bg-background shadow-lg scrollbar-thin">
                    {visibleIndustries.map((ind, i) => (
                      <button
                        type="button"
                        key={ind.id}
                        onClick={() => {
                          onChange({ industry: ind.id });
                          setIndustrySearch('');
                          setHighlightedIndex(-1);
                        }}
                        onMouseEnter={() => setHighlightedIndex(i)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between ${
                          i === highlightedIndex ? 'bg-muted' : 'hover:bg-muted'
                        }`}
                      >
                        <span>{ind.label}</span>
                        <span className="text-xs text-muted-foreground">{ind.category}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        onChange({ industry: 'other', industryOther: industrySearch });
                        setIndustrySearch('');
                        setHighlightedIndex(-1);
                      }}
                      onMouseEnter={() => setHighlightedIndex(visibleIndustries.length)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between text-muted-foreground ${
                        highlightedIndex === visibleIndustries.length ? 'bg-muted' : 'hover:bg-muted'
                      }`}
                    >
                      <span>Other / Custom</span>
                      <span className="text-xs">"{industrySearch}"</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {POPULAR_INDUSTRY_DATA.map((ind) => (
                  <button
                    type="button"
                    key={ind.id}
                    onClick={() => onChange({ industry: ind.id })}
                    className="px-4 py-2 text-sm rounded-full border border-border hover:border-primary hover:bg-primary/5 hover:text-primary transition-all font-medium"
                  >
                    {ind.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => onChange({ industry: 'other' })}
                  className="px-4 py-2 text-sm rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-primary transition-all"
                >
                  Other...
                </button>
              </div>
            </>
          )}

          {data.industry === 'other' && (
            <Input
              placeholder="e.g. Photography, Catering, Tutoring"
              value={data.industryOther}
              onChange={(e) => onChange({ industryOther: e.target.value })}
              autoFocus
              className="h-12 rounded-xl"
              autoComplete="off"
              data-1p-ignore
            />
          )}
        </div>

        {/* Logo Upload with Drag & Drop */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            Business Logo <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          {data.logoPreview ? (
            <div className="flex items-center gap-4 p-4 border rounded-xl bg-muted/20">
              <img src={data.logoPreview} alt="Logo" className="h-12 w-12 object-cover rounded-xl border border-border/50" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{data.logoFile?.name}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive flex-shrink-0"
                onClick={() => {
                  onChange({ logoFile: null, logoPreview: null, logoExtractedColors: [] });
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={`w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
                isDragActive
                  ? 'border-primary bg-primary/5 scale-[1.01]'
                  : 'border-muted-foreground/20 hover:border-primary/40 hover:bg-primary/[0.02]'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className={`h-6 w-6 mx-auto mb-2 transition-colors ${
                isDragActive ? 'text-primary' : 'text-muted-foreground/40'
              }`} />
              <p className={`text-sm font-medium transition-colors ${
                isDragActive ? 'text-primary' : 'text-muted-foreground'
              }`}>
                {isDragActive ? 'Drop your logo here' : 'Drag & drop or click to upload'}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">PNG, JPG, or WebP — max 5MB</p>
            </div>
          )}
          {logoError && (
            <p className="text-xs text-destructive mt-1.5">{logoError}</p>
          )}
        </div>
      </div>

    </>
  );
}

// ---------------------------------------------------------------------------
// Template Carousel: shadcn Carousel with infinite loop
// ---------------------------------------------------------------------------

function TemplateCarousel({
  templates,
  selectedSlug,
  primaryColor,
  onSelect,
}: {
  templates: TemplateMetadata[];
  selectedSlug: string;
  primaryColor: string;
  onSelect: (slug: string) => void;
}) {
  return (
    <Carousel
      opts={{ loop: true, align: 'start', dragFree: true }}
      className="w-full"
    >
      <div className="flex items-center justify-between mb-2.5">
        <Label className="text-xs font-medium text-muted-foreground">Template</Label>
        <div className="flex items-center gap-1">
          <CarouselPrevious className="static translate-x-0 translate-y-0 size-7 rounded-lg border-border/40 bg-background hover:bg-muted shadow-sm" />
          <CarouselNext className="static translate-x-0 translate-y-0 size-7 rounded-lg border-border/40 bg-background hover:bg-muted shadow-sm" />
        </div>
      </div>
      <CarouselContent className="-ml-3">
        {templates.map((template) => (
          <CarouselItem key={template.slug} className="pl-3 basis-1/4 py-1">
            <TemplateThumbnail
              template={template}
              isSelected={selectedSlug === template.slug}
              primaryColor={primaryColor}
              onSelect={() => onSelect(template.slug)}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
}

// ---------------------------------------------------------------------------
// Template Thumbnail: SVG preview (matches template designer)
// ---------------------------------------------------------------------------

function TemplateThumbnail({
  template,
  isSelected,
  primaryColor,
  onSelect,
}: {
  template: TemplateMetadata;
  isSelected: boolean;
  primaryColor: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full group"
    >
      <div
        className={`aspect-[4/5] rounded-lg overflow-hidden border-2 transition-all duration-200 relative bg-white ${
          isSelected
            ? 'border-primary shadow-md shadow-primary/10'
            : 'border-border/60 group-hover:border-primary/40 group-hover:shadow-sm'
        }`}
      >
        {template.preview_svg ? (
          <div
            className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
            style={{
              ['--theme-primary' as string]: primaryColor,
              ['--theme-secondary' as string]: `${primaryColor}10`,
              ['--theme-accent' as string]: `${primaryColor}1a`,
              ['--theme-table-border' as string]: `${primaryColor}30`,
              ['--theme-dark' as string]: primaryColor,
            }}
            dangerouslySetInnerHTML={{ __html: template.preview_svg }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted/20">
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        )}
        {isSelected && (
          <div className="absolute top-1.5 right-1.5 z-10 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
            <Check className="w-2.5 h-2.5" />
          </div>
        )}
      </div>
      <p className={`text-[10px] text-center truncate font-medium mt-1.5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
        {template.metadata.name}
      </p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Custom Color Picker (react-colorful popover)
// ---------------------------------------------------------------------------

function CustomColorPicker({
  value,
  isCustom,
  onChange,
}: {
  value: string;
  isCustom: boolean;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`relative w-7 h-7 rounded-full cursor-pointer transition-all duration-150 border-[1.5px] border-dashed border-muted-foreground/30 hover:border-primary/50 hover:scale-105 flex items-center justify-center ${
          isCustom ? 'ring-2 ring-offset-1 ring-primary scale-110' : ''
        }`}
        style={isCustom ? { backgroundColor: value } : undefined}
        title="Custom color"
      >
        {isCustom ? (
          <Check className="w-3 h-3 text-white drop-shadow" />
        ) : (
          <span className="text-xs text-muted-foreground font-bold leading-none">+</span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 bottom-full mb-3 right-0 bg-background border rounded-xl shadow-xl p-3">
          <HexColorPicker color={value} onChange={onChange} />
          <div className="flex items-center gap-2 mt-2.5 px-0.5">
            <div className="w-5 h-5 rounded-full border shrink-0" style={{ backgroundColor: value }} />
            <span className="text-xs font-mono text-muted-foreground uppercase">{value}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Your Style (template + colors with logo-generated theme)
// ---------------------------------------------------------------------------

function StepYourStyle({
  data,
  onChange,
  templates,
  templatesLoading,
}: {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
  templates: TemplateMetadata[];
  templatesLoading: boolean;
}) {

  // Auto-apply logo theme on first visit if we have extracted colors
  const [hasAppliedLogoTheme, setHasAppliedLogoTheme] = useState(false);
  useEffect(() => {
    if (!hasAppliedLogoTheme && data.logoExtractedColors.length > 0) {
      const primaryCandidate = data.logoExtractedColors[0];
      const theme = generateThemeFromPrimary(primaryCandidate.hex);
      onChange({ colorTheme: theme.primary });
      setHasAppliedLogoTheme(true);
    }
  }, [data.logoExtractedColors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build color swatches: logo-extracted colors first, then presets
  const logoColorSwatches = data.logoExtractedColors.map((c, i) => ({
    id: `logo-${i}`,
    hex: generateThemeFromPrimary(c.hex).primary,
    name: i === 0 ? 'From Logo' : `Logo ${i + 1}`,
    isLogo: true,
  }));

  const allSwatches = [
    ...logoColorSwatches,
    ...COLOR_THEMES.map((c) => ({ ...c, isLogo: false })),
  ];

  return (
    <>
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Choose your style
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          You can always change this later.
        </p>
      </div>

      <div className="space-y-6">
        {/* Template carousel */}
        <div>
          {templatesLoading ? (
            <div>
              <Label className="text-xs font-medium text-muted-foreground mb-2.5 block">Template</Label>
              <div className="flex gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-1 space-y-1.5">
                    <div className="aspect-[4/5] rounded-lg bg-muted animate-pulse" />
                    <div className="h-3 w-2/3 mx-auto rounded bg-muted animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <TemplateCarousel
              templates={templates.slice(0, 10)}
              selectedSlug={data.templateSlug}
              primaryColor={data.colorTheme}
              onSelect={(slug) => onChange({ templateSlug: slug })}
            />
          )}
        </div>

        {/* Color picker — logo colors inline with presets + custom */}
        <div className="space-y-2.5">
          <Label className="text-xs font-medium text-muted-foreground">Color Theme</Label>
          <div className="flex flex-wrap gap-[10px]">
            {allSwatches.map((swatch) => (
              <button
                type="button"
                key={swatch.id}
                onClick={() => onChange({ colorTheme: swatch.hex })}
                className={`relative w-7 h-7 rounded-full transition-all duration-150 ${
                  data.colorTheme === swatch.hex
                    ? 'ring-2 ring-offset-1 ring-primary scale-110'
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: swatch.hex }}
                title={swatch.name}
              >
                {data.colorTheme === swatch.hex && (
                  <Check className="w-3 h-3 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow" />
                )}
              </button>
            ))}
            {/* Custom color picker */}
            <CustomColorPicker
              value={data.colorTheme}
              isCustom={!allSwatches.some((s) => s.hex === data.colorTheme)}
              onChange={(hex) => onChange({ colorTheme: hex })}
            />
          </div>
        </div>
      </div>

    </>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Setting Up (animated loader)
// ---------------------------------------------------------------------------

function StepSettingUp({ onComplete }: { onComplete: () => void }) {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  const setupSteps = [
    { label: 'Creating your business' },
    { label: 'Customizing your templates' },
    { label: 'Preparing your dashboard' },
    { label: 'Finalizing' },
  ];

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const stepDurations = [2400, 2800, 3600, 2300];
    let offset = 0;
    setupSteps.forEach((_, i) => {
      timers.push(setTimeout(() => setActiveStep(i), offset));
      timers.push(setTimeout(() => setCompletedSteps((prev) => [...prev, i]), offset + stepDurations[i] - 500));
      offset += stepDurations[i];
    });
    // Advance right after the last checkmark
    timers.push(setTimeout(() => onComplete(), offset + 400));
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="mb-10 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Sparkles className="h-8 w-8 text-primary animate-pulse" />
        </div>
        <h2 className="text-3xl font-bold text-foreground tracking-tight">
          Setting up your account
        </h2>
        <p className="text-muted-foreground mt-2 text-base">This will only take a moment</p>
      </div>

      <div className="w-full max-w-sm space-y-3">
        {setupSteps.map((step, i) => {
          const isComplete = completedSteps.includes(i);
          const isActive = activeStep === i && !isComplete;
          const isWaiting = activeStep < i;

          return (
            <div
              key={step.label}
              className={`flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-500 ${
                isComplete
                  ? 'bg-primary/5 border border-primary/15'
                  : isActive
                    ? 'bg-muted/50 border border-border'
                    : 'border border-transparent'
              }`}
              style={{
                opacity: isWaiting ? 0.3 : 1,
                transform: isWaiting ? 'translateY(4px)' : 'translateY(0)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="flex-shrink-0">
                {isComplete ? (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-md shadow-primary/20">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                  </div>
                )}
              </div>
              <span
                className={`text-sm font-medium transition-colors duration-300 ${
                  isComplete ? 'text-primary' : 'text-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Start Subscription (left pane — conversion-focused)
// ---------------------------------------------------------------------------

// Parse 2-letter country code from Google Places address string, fallback 'US'
const COUNTRY_MAP: Record<string, string> = {
  'usa': 'US', 'us': 'US', 'united states': 'US',
  'uk': 'GB', 'united kingdom': 'GB', 'great britain': 'GB',
  'canada': 'CA', 'ca': 'CA',
  'australia': 'AU', 'au': 'AU',
  'new zealand': 'NZ', 'nz': 'NZ',
  'ireland': 'IE', 'ie': 'IE',
  'mexico': 'MX', 'mx': 'MX',
};

function parseCountryCode(address: string): string {
  const last = address.split(',').pop()?.trim().toLowerCase();
  if (!last) return 'US';
  // Check if it's already a 2-letter code
  if (last.length === 2) return last.toUpperCase();
  return COUNTRY_MAP[last] || 'US';
}

interface StepTrialSignupProps {
  clientSecret: string | null;
  /** 'setup' = free trial (SetupIntent), 'payment' = charge today (PaymentIntent). */
  mode: OnboardingCheckoutMode;
  trialDays: number;
  subscriptionId: string | null;
  defaultCountry: string;
  selectedPlan: string;
  billingCycle: 'monthly' | 'annual';
  onPlanChange: (plan: string) => void;
  onBillingCycleChange: (interval: 'monthly' | 'annual') => void;
  /** Payment mode: verify the paid subscription. */
  onPaymentComplete: (subscriptionId: string) => Promise<void>;
  /** Setup mode: start the trial with the confirmed payment method. */
  onSetupComplete: (paymentMethodId: string) => Promise<void>;
  submitting: boolean;
  submitError: string;
}

function StepTrialSignupInner({
  defaultCountry,
  mode,
  trialDays,
  subscriptionId,
  selectedPlan,
  billingCycle,
  onPlanChange,
  onBillingCycleChange,
  onPaymentComplete,
  onSetupComplete,
  submitting,
  submitError,
}: Omit<StepTrialSignupProps, 'clientSecret'>) {
  const stripe = useStripe();
  const elements = useElements();
  const recommendedPlan = 'premium';
  const [planOpen, setPlanOpen] = useState(false);
  const planDropdownRef = useRef<HTMLDivElement>(null);
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  const displayPlans = pricingPlans.filter((p) =>
    ['base', 'premium', 'premium_plus'].includes(p.id)
  );
  const isAnnual = billingCycle === 'annual';
  const selectedPlanData = displayPlans.find((p) => p.id === selectedPlan);
  const price = selectedPlanData
    ? isAnnual ? selectedPlanData.pricing.annually : selectedPlanData.pricing.monthly
    : 0;
  const firstCharge = isAnnual ? price * 12 : price;
  const isTrial = mode === 'setup' && trialDays > 0;
  const dueToday = isTrial ? 0 : firstCharge;
  const trialEndStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + trialDays);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, [trialDays]);

  // Close plan dropdown on outside click
  useEffect(() => {
    if (!planOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (planDropdownRef.current && !planDropdownRef.current.contains(e.target as Node)) {
        setPlanOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [planOpen]);

  const handleStartSubscription = async () => {
    if (!stripe || !elements || processing || submitting) return;
    if (mode === 'payment' && !subscriptionId) return;
    setCardError('');
    setProcessing(true);

    try {
      // Keep checkout context through redirects for 3DS/SCA and wallet flows.
      const returnUrl = new URL('/onboarding', window.location.origin);
      returnUrl.searchParams.set('plan', selectedPlan);
      returnUrl.searchParams.set('interval', billingCycle);
      returnUrl.searchParams.set('mode', mode);
      if (subscriptionId) returnUrl.searchParams.set('subscription_id', subscriptionId);

      if (mode === 'setup') {
        // Free trial: save the card now, charge when the trial ends.
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: returnUrl.toString() },
          redirect: 'if_required',
        });

        if (error) {
          setCardError(error.message || 'Card validation failed');
          return;
        }

        const paymentMethodId =
          typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;

        if (setupIntent?.status === 'succeeded' && paymentMethodId) {
          await onSetupComplete(paymentMethodId);
        } else if (setupIntent?.status === 'processing') {
          setCardError('Your payment method is still being verified. We will finish setup as soon as Stripe confirms it.');
        } else {
          setCardError('We could not save your payment method. Please check the details and try again.');
        }
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl.toString() },
        redirect: 'if_required',
      });

      if (error) {
        setCardError(error.message || 'Card validation failed');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        await onPaymentComplete(subscriptionId as string);
      } else if (paymentIntent?.status === 'processing') {
        setCardError('Your payment is still processing. We will finish setup as soon as Stripe confirms it.');
      } else {
        setCardError('Payment was not completed. Please check your payment method and try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const errorMsg = cardError || submitError;

  return (
    <>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground tracking-tight">
          Your account is almost ready!
        </h2>
        <p className="text-muted-foreground mt-2 text-base">
          {isTrial ? (
            <>Start your free {trialDays}-day trial, pay <b>$0</b> today.</>
          ) : (
            <>Choose a plan and start your subscription today.</>
          )}
        </p>
      </div>

      {/* Unified checkout card */}
      <div className="border border-border rounded-xl mb-5 text-sm divide-y divide-border">
        {/* Plan & billing combined */}
        <div className="relative px-4 py-3" ref={planDropdownRef}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground text-sm">Plan</span>
            <div className="flex bg-muted rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => onBillingCycleChange('monthly')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  !isAnnual ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => onBillingCycleChange('annual')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  isAnnual ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Annual
                {selectedPlanData && (
                  <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-100 px-1 py-px rounded">
                    Save {Math.round((1 - selectedPlanData.pricing.annually / selectedPlanData.pricing.monthly) * 100)}%
                  </span>
                )}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPlanOpen(!planOpen)}
            className="w-full flex items-center justify-between px-3 py-2.5 border border-border rounded-lg hover:border-foreground/20 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">{selectedPlanData?.name}</span>
              {selectedPlan === recommendedPlan && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">Recommended</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground text-xs">${price}/mo {isAnnual ? '(billed annually)' : '(billed monthly)'}</span>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${planOpen ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {planOpen && (
            <div className="absolute z-20 left-2 right-2 top-full mt-1 border rounded-xl divide-y bg-background shadow-lg overflow-hidden">
              {displayPlans.map((plan) => {
                const planPrice = isAnnual ? plan.pricing.annually : plan.pricing.monthly;
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => { onPlanChange(plan.id); setPlanOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                    }`}>
                      {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{plan.name}</span>
                        {plan.id === recommendedPlan && (
                          <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">Recommended</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{plan.features[0]}</p>
                    </div>
                    <span className="text-sm font-medium flex-shrink-0">${planPrice}/mo</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected plan benefits — collapsed by default */}
        {selectedPlanData && (
          <div className="px-4">
            <button
              type="button"
              onClick={() => setFeaturesOpen(!featuresOpen)}
              className="flex items-center justify-between w-full py-3 text-left"
            >
              <span className="text-muted-foreground text-xs font-medium">What's included</span>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
            </button>
            {featuresOpen && (
              <div className="flex flex-col gap-1.5 pb-3">
                {[
                  ...(selectedPlanData.cumulativeFeatures || []),
                  ...selectedPlanData.features,
                  'Advanced document designer',
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-1.5">
                    <Check className="h-3 w-3 text-primary flex-shrink-0" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment method — real Stripe Elements */}
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-muted-foreground">Payment method</span>
          </div>
          <PaymentElement options={{
            layout: { type: 'tabs', defaultCollapsed: false, radios: false, spacedAccordionItems: false },
            fields: { billingDetails: { address: { country: 'auto', postalCode: 'auto' } } },
            paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'cashapp'],
            terms: { card: 'never', applePay: 'never', googlePay: 'never', cashapp: 'never', paypal: 'never', auBecsDebit: 'never', bancontact: 'never', ideal: 'never', sepaDebit: 'never', sofort: 'never', usBankAccount: 'never' },
            defaultValues: { billingDetails: { address: { country: defaultCountry } } },
          }} />
          {errorMsg && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-3">
              {errorMsg}
            </div>
          )}
        </div>

        {/* Billing summary row */}
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-muted-foreground">Billing</span>
          <span className="text-muted-foreground">{isAnnual ? 'Annual' : 'Monthly'}</span>
        </div>

        {isTrial && (
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-muted-foreground">Trial ends</span>
            <span className="text-muted-foreground">
              {trialEndStr} · then ${firstCharge.toFixed(2)}{isAnnual ? '/yr' : '/mo'}
            </span>
          </div>
        )}

        {/* Due today */}
        <div className="flex items-center justify-between px-4 py-3 bg-muted/20">
          <span className="font-semibold">Due today</span>
          <span className="text-emerald-600 text-xl font-bold">${dueToday.toFixed(2)}</span>
        </div>
      </div>

      {/* CTA button — inside the inner component so it can access stripe/elements */}
      <div className="mt-5">
        <p className="text-center text-xs text-muted-foreground mb-3">
          {isTrial
            ? `Your card won't be charged until ${trialEndStr}. Cancel anytime before then and pay nothing.`
            : 'Your subscription starts today. Cancel anytime.'}
        </p>
        <Button
          onClick={handleStartSubscription}
          disabled={!stripe || !elements || (mode === 'payment' && !subscriptionId) || submitting || processing}
          className="w-full h-12 text-base rounded-xl font-semibold"
          size="lg"
        >
          {(submitting || processing) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : isTrial ? (
            'Start free trial'
          ) : (
            'Subscribe now'
          )}
        </Button>
        <div className="flex items-center justify-center gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">256-bit SSL encryption</span>
          </div>
          <div className="text-border">|</div>
          <div className="flex items-center gap-1.5">
            <LockKeyhole className="h-3 w-3 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">Secured by <span className="font-semibold">Stripe</span></span>
          </div>
        </div>
      </div>
    </>
  );
}

function StepTrialSignup({
  clientSecret,
  mode,
  trialDays,
  subscriptionId,
  defaultCountry,
  selectedPlan,
  billingCycle,
  onPlanChange,
  onBillingCycleChange,
  onPaymentComplete,
  onSetupComplete,
  submitting,
  submitError,
}: StepTrialSignupProps) {
  if (!clientSecret) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Preparing secure payment...</p>
        {submitError && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
            {submitError}
          </div>
        )}
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          inputs: 'condensed',
          variables: {
            colorPrimary: '#2563EB',
            fontSizeBase: '14px',
            spacingGridRow: '12px',
            borderRadius: '6px',
          },
          rules: {
            '.Tab': {
              padding: '8px 12px',
              fontSize: '12px',
            },
            '.Tab--selected': {
              borderColor: '#2563EB',
              boxShadow: '0 0 0 1px #2563EB',
            },
            '.TabIcon': {
              width: '16px',
              height: '16px',
            },
            '.Input:focus': {
              borderColor: '#2563EB',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.2)',
            },
          },
        },
      }}
    >
      <StepTrialSignupInner
        mode={mode}
        trialDays={trialDays}
        subscriptionId={subscriptionId}
        defaultCountry={defaultCountry}
        selectedPlan={selectedPlan}
        billingCycle={billingCycle}
        onPlanChange={onPlanChange}
        onBillingCycleChange={onBillingCycleChange}
        onPaymentComplete={onPaymentComplete}
        onSetupComplete={onSetupComplete}
        submitting={submitting}
        submitError={submitError}
      />
    </Elements>
  );
}

// ---------------------------------------------------------------------------
// Main Onboarding Component
// ---------------------------------------------------------------------------

// Helper: merge addressUnit into address string (same as mobile FocusedAddressInput)
function mergeAddressUnit(address: string, unit: string): string {
  if (!unit.trim()) return address;
  const lines = address.split('\n');
  if (lines.length > 0) {
    lines[0] = `${lines[0]}, ${unit.trim()}`;
  }
  return lines.join('\n');
}

export function Onboarding() {
  const navigate = useNavigate();

  // Capture Stripe redirect params once on mount (3DS, wallets, etc.)
  // Stripe adds payment_intent_client_secret / setup_intent_client_secret + redirect_status;
  // we add plan, interval, mode and (payment mode) subscription_id.
  const [redirectData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentSecret = params.get('payment_intent_client_secret');
    const setupSecret = params.get('setup_intent_client_secret');
    const redirectStatus = params.get('redirect_status');
    const plan = params.get('plan');
    const interval = params.get('interval') as 'monthly' | 'annual' | null;
    const subscriptionId = params.get('subscription_id');
    const mode: OnboardingCheckoutMode = setupSecret ? 'setup' : 'payment';
    const clientSecret = setupSecret || paymentSecret;
    const hasContext = mode === 'setup' ? true : !!subscriptionId;
    if (clientSecret && redirectStatus && plan && interval && hasContext) {
      window.history.replaceState({}, '', '/onboarding');
      return { mode, clientSecret, redirectStatus, plan, interval, subscriptionId };
    }
    return null;
  });
  const redirectHandled = useRef(false);

  const [currentStep, setCurrentStep] = useState(redirectData ? 5 : 1);
  const [data, setData] = useState<OnboardingData>(INITIAL_DATA);
  const [transitioning, setTransitioning] = useState(false);
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  // Save-as-you-go state
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(
    redirectData?.clientSecret ?? null
  );
  const [subscriptionId, setSubscriptionId] = useState<string | null>(
    redirectData?.subscriptionId ?? null
  );
  const [selectedPlan, setSelectedPlan] = useState<string>(redirectData?.plan ?? 'base');
  // Default to monthly: most abandoned checkouts were on the annual price, and the
  // "I only needed one invoice" cancellations were annual signups.
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(redirectData?.interval ?? 'monthly');
  const [checkoutMode, setCheckoutMode] = useState<OnboardingCheckoutMode>(redirectData?.mode ?? 'payment');
  const [trialDays, setTrialDays] = useState(0);
  const subscriptionRequestKey = useRef<string | null>(null);
  const [replacePendingSubscription, setReplacePendingSubscription] = useState(false);
  const [trialSubmitting, setTrialSubmitting] = useState(!!redirectData);
  const [trialError, setTrialError] = useState('');

  useEffect(() => {
    authApi.getMe().then(async (meRes) => {
      if (!meRes.data || meRes.data.businesses.length === 0) return;
      const firstBusinessId = meRes.data.businesses[0].id;
      const billingRes = await billingApi.getStatus(firstBusinessId);
      const billingStatus = billingRes.data;
      const needsSubscription =
        billingStatus &&
        !billingStatus.is_legacy &&
        (
          !billingStatus.has_stripe_subscription ||
          ['incomplete', 'incomplete_expired', 'unpaid'].includes(billingStatus.subscription_status || '')
        );
      if (needsSubscription && billingStatus) {
        setBusinessId(firstBusinessId);
        if (
          billingStatus.plan &&
          billingStatus.plan !== 'free_trial' &&
          ['base', 'premium', 'premium_plus'].includes(billingStatus.plan)
        ) {
          setSelectedPlan(billingStatus.plan);
        }
        if (billingStatus.billing_interval === 'monthly' || billingStatus.billing_interval === 'annual') {
          setBillingCycle(billingStatus.billing_interval);
        }
        setCurrentStep(5);
      } else {
        navigate(`/${firstBusinessId}/dashboard`);
      }
    });
  }, [navigate]);

  // Preload templates on mount so they're ready by step 3
  useEffect(() => {
    documentsApi.getTemplateBundle({}).then((response) => {
      if (response.data?.templates?.length) setTemplates(response.data.templates);
      else throw new Error('No templates in bundle');
      setTemplatesLoading(false);
    }).catch(() => {
      documentsApi.listTemplates('document').then((response) => {
        if (response.data?.templates) setTemplates(response.data.templates);
        setTemplatesLoading(false);
      });
    });
  }, []);

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const goToStep = useCallback((step: number) => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentStep(step);
      setTransitioning(false);
      window.scrollTo(0, 0);
    }, 250);
  }, []);

  // --- Save-as-you-go: Continue handlers per step ---

  const handleStep1Continue = useCallback(async () => {
    setSaving(true);
    try {
      await authApi.updateMe({ first_name: data.firstName.trim(), last_name: data.lastName.trim() });
      const mergedAddress = mergeAddressUnit(data.address, data.addressUnit);
      const response = await businessApi.create({
        name: data.businessName.trim(),
        address: mergedAddress,
        address_latitude: data.addressCoords?.latitude,
        address_longitude: data.addressCoords?.longitude,
        industry: data.industry || 'other',
      });
      if (response.data) {
        setBusinessId(response.data.id);
      }
      goToStep(2);
    } catch (err) {
      console.error('Step 1 save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [data, goToStep]);

  const handleStep2Continue = useCallback(async () => {
    if (!businessId) { goToStep(3); return; }
    setSaving(true);
    try {
      await businessApi.update(businessId, {
        industry: data.industry,
        industry_other: data.industry === 'other' ? data.industryOther : undefined,
      });
      // Upload logo if provided
      if (data.logoFile) {
        try {
          const uploadResponse = await uploadApi.uploadLogo(data.logoFile);
          if (uploadResponse.data?.url) {
            await businessApi.update(businessId, { logo: uploadResponse.data.url });
          }
        } catch { /* logo upload is optional, continue */ }
      }
      goToStep(3);
    } catch (err) {
      console.error('Step 2 save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [data, businessId, goToStep]);

  const handleStep3Continue = useCallback(async () => {
    if (!businessId) { goToStep(4); return; }
    setSaving(true);
    try {
      const colorTheme = generateThemeFromPrimary(data.colorTheme);
      await templateConfigApi.save(businessId, {
        template_name: data.templateSlug,
        settings: { colorTheme },
      });
      goToStep(4);
    } catch (err) {
      console.error('Step 3 save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [data, businessId, goToStep]);

  const handlePlanChange = useCallback((plan: string) => {
    setSelectedPlan(plan);
    setPaymentClientSecret(null);
    setSubscriptionId(null);
    setReplacePendingSubscription(!!subscriptionId);
    subscriptionRequestKey.current = null;
    setTrialError('');
  }, [subscriptionId]);

  const handleBillingCycleChange = useCallback((interval: 'monthly' | 'annual') => {
    setBillingCycle(interval);
    setPaymentClientSecret(null);
    setSubscriptionId(null);
    setReplacePendingSubscription(!!subscriptionId);
    subscriptionRequestKey.current = null;
    setTrialError('');
  }, [subscriptionId]);

  // Create the first-invoice PaymentIntent when entering the subscription step.
  useEffect(() => {
    if (currentStep === 5 && !paymentClientSecret && !redirectData) {
      if (!businessId) {
        goToStep(1);
        return;
      }
      const requestKey = `${selectedPlan}:${billingCycle}`;
      if (subscriptionRequestKey.current === requestKey) return;
      subscriptionRequestKey.current = requestKey;
      setTrialError('');
      billingApi.createOnboardingSubscription(selectedPlan, billingCycle, replacePendingSubscription).then((response) => {
        if (response.data?.client_secret) {
          if (response.data.plan && ['base', 'premium', 'premium_plus'].includes(response.data.plan)) {
            setSelectedPlan(response.data.plan);
          }
          if (response.data.interval === 'monthly' || response.data.interval === 'annual') {
            setBillingCycle(response.data.interval);
          }
          setCheckoutMode(response.data.mode === 'setup' ? 'setup' : 'payment');
          setTrialDays(response.data.trial_days ?? 0);
          setPaymentClientSecret(response.data.client_secret);
          setSubscriptionId(response.data.subscription_id);
          setReplacePendingSubscription(false);
        } else if (response.error) {
          setTrialError(response.error);
        }
      }).catch((err) => {
        console.error('Failed to create onboarding subscription:', err);
        setTrialError('Failed to initialize subscription. Please try again.');
      });
    }
  }, [currentStep, paymentClientSecret, redirectData, businessId, selectedPlan, billingCycle, replacePendingSubscription, goToStep]);

  const goToDashboard = useCallback(() => {
    if (businessId) {
      navigate(`/${businessId}/dashboard?welcome=true`);
    } else {
      navigate('/?welcome=true');
    }
  }, [businessId, navigate]);

  // Payment mode: verify subscription after Stripe confirms the first invoice payment.
  const handlePaymentComplete = useCallback(async (stripeSubscriptionId: string) => {
    setTrialSubmitting(true);
    setTrialError('');
    const response = await billingApi.verifyOnboardingSubscription(stripeSubscriptionId);
    if (response.error) {
      setTrialError(response.error);
      setTrialSubmitting(false);
      return;
    }
    if (response.data) goToDashboard();
  }, [goToDashboard]);

  // Setup mode: the card is saved; start the trial with that payment method attached.
  const handleSetupComplete = useCallback(async (paymentMethodId: string, plan = selectedPlan, interval = billingCycle) => {
    setTrialSubmitting(true);
    setTrialError('');
    const response = await billingApi.activateTrial(plan, interval, paymentMethodId);
    if (response.error) {
      setTrialError(response.error);
      setTrialSubmitting(false);
      return;
    }
    if (response.data) goToDashboard();
  }, [selectedPlan, billingCycle, goToDashboard]);

  // Complete checkout after Stripe redirect return (3DS, wallets, etc.)
  // Waits for businessId from getMe, then verifies with Stripe and the backend.
  useEffect(() => {
    if (!redirectData || !businessId || redirectHandled.current) return;
    redirectHandled.current = true;

    if (redirectData.mode === 'setup') {
      stripePromise.then((stripe) => {
        if (!stripe) throw new Error('Stripe failed to load');
        return stripe.retrieveSetupIntent(redirectData.clientSecret);
      }).then((result) => {
        const si = result?.setupIntent;
        const paymentMethodId = typeof si?.payment_method === 'string' ? si.payment_method : si?.payment_method?.id;
        if (si?.status === 'succeeded' && paymentMethodId) {
          handleSetupComplete(paymentMethodId, redirectData.plan, redirectData.interval);
        } else if (si?.status === 'processing') {
          setTrialError('Your payment method is still being verified. We will finish setup as soon as Stripe confirms it.');
          setTrialSubmitting(false);
        } else {
          setTrialError('We could not save your payment method. Please try again.');
          setTrialSubmitting(false);
        }
      }).catch(() => {
        setTrialError('Could not verify your payment method. Please try again.');
        setTrialSubmitting(false);
      });
      return;
    }

    stripePromise.then((stripe) => {
      if (!stripe) throw new Error('Stripe failed to load');
      return stripe.retrievePaymentIntent(redirectData.clientSecret);
    }).then((result) => {
      if (result?.paymentIntent?.status === 'succeeded') {
        handlePaymentComplete(redirectData.subscriptionId as string);
      } else if (result?.paymentIntent?.status === 'processing') {
        setTrialError('Your payment is still processing. We will finish setup as soon as Stripe confirms it.');
        setTrialSubmitting(false);
      } else if (result?.paymentIntent?.status === 'requires_payment_method') {
        setTrialError('Payment was not completed. Please check your payment method and try again.');
        setTrialSubmitting(false);
      } else {
        setTrialError('Payment verification failed. Please try again.');
        setTrialSubmitting(false);
      }
    }).catch(() => {
      setTrialError('Could not verify payment. Please try again.');
      setTrialSubmitting(false);
    });
  }, [redirectData, businessId, handlePaymentComplete, handleSetupComplete]);

  // Only step 4 (loader) is full-screen
  if (currentStep === 4) {
    return (
      <div
        className={`transition-all duration-300 ${
          transitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <StepSettingUp onComplete={() => goToStep(5)} />
      </div>
    );
  }

  const isTrialStep = currentStep === 5;

  // Step continue handler map
  const handleContinue = () => {
    if (currentStep === 1) handleStep1Continue();
    else if (currentStep === 2) handleStep2Continue();
    else if (currentStep === 3) handleStep3Continue();
    else goToStep(currentStep + 1);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left pane: Form — full width on mobile/tablet, fixed width when right pane shows */}
      <div className="w-full md:w-[420px] lg:w-[520px] xl:w-[560px] md:flex-none flex flex-col min-h-screen">
        {/* Top bar: logo + step counter */}
        <div className="px-8 lg:px-12 pt-8 pb-6 space-y-6">
          <div className="flex items-center justify-between">
            <img src="/header.svg" alt="InvoiceOwl" className="h-8" />
            {!isTrialStep && (
              <span className="text-xs text-muted-foreground font-medium bg-muted px-2.5 py-1 rounded-full">
                {currentStep}/3
              </span>
            )}
          </div>
          {!isTrialStep && <ProgressBar currentStep={currentStep} />}
        </div>

        {/* Form content — top-aligned, centered on mobile/tablet */}
        <div className="flex-1 px-8 lg:px-12 overflow-y-auto">
          <div
            className={`transition-all duration-300 ${
              transitioning ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'
            }`}
          >
            {currentStep === 1 && (
              <StepAboutYou data={data} onChange={updateData} />
            )}
            {currentStep === 2 && (
              <StepYourBusiness data={data} onChange={updateData} />
            )}
            {currentStep === 3 && (
              <StepYourStyle data={data} onChange={updateData} templates={templates} templatesLoading={templatesLoading} />
            )}
            {currentStep === 5 && (
              <StepTrialSignup
                clientSecret={paymentClientSecret}
                subscriptionId={subscriptionId}
                defaultCountry={parseCountryCode(data.address)}
                selectedPlan={selectedPlan}
                billingCycle={billingCycle}
                onPlanChange={handlePlanChange}
                onBillingCycleChange={handleBillingCycleChange}
                mode={checkoutMode}
                trialDays={trialDays}
                onPaymentComplete={handlePaymentComplete}
                onSetupComplete={handleSetupComplete}
                submitting={trialSubmitting}
                submitError={trialError}
              />
            )}
          </div>
        </div>

        {/* Nav buttons — pinned to bottom of left pane (steps 1-3 only) */}
        {!isTrialStep && (
          <div className="shrink-0 px-8 lg:px-12 pt-3 pb-6 border-t border-border/40 bg-background">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => goToStep(currentStep - 1)}
                disabled={currentStep === 1 || saving}
                className="h-12 px-6 rounded-xl"
                size="lg"
              >
                Back
              </Button>
              <Button
                onClick={handleContinue}
                disabled={
                  saving ||
                  (currentStep === 1
                    ? !(data.firstName.trim() && data.lastName.trim() && data.businessName.trim() && data.address.trim())
                    : currentStep === 2
                    ? !data.industry
                    : false)
                }
                className="flex-1 h-12 text-base font-semibold rounded-xl shadow-sm"
                size="lg"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Right pane: Live preview */}
      <RightPane data={data} currentStep={currentStep} />
    </div>
  );
}
